/**
 * Menu Permission Configuration
 * Defines permission requirements for each menu item
 * Separated from menu.ts for cleaner code organization
 * 
 * @author JGS Tech Team
 * @version 1.0
 */

import type { MenuPermissionConfig } from '../types/accessControl';
import { PERMISSION_CODES } from '../types/accessControl';

/**
 * Permission configuration for dashboard menu items
 * Key corresponds to menu item id in dashboardPagesMenu
 */
export const dashboardMenuPermissions: Record<string, MenuPermissionConfig> = {
    // Analytics Dashboard - viewable by editors and above
    analytics: {
        anyPermissions: [PERMISSION_CODES.ANALYTICS_VIEW, PERMISSION_CODES.DASHBOARD_VIEW],
        anyRoles: ['ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Manage Users - admin only
    manageUsers: {
        permission: PERMISSION_CODES.USERS_VIEW,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Manage Articles - editors and above can view
    manageArticles: {
        anyPermissions: [PERMISSION_CODES.ARTICLES_VIEW_ALL, PERMISSION_CODES.ARTICLES_VIEW_OWN],
        anyRoles: ['ROLE_AUTHOR', 'ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Create Article - authors and above
    createNewArticle: {
        permission: PERMISSION_CODES.ARTICLES_CREATE,
        anyRoles: ['ROLE_AUTHOR', 'ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Campaign Management - publishers and admin
    campaignManagement: {
        permission: PERMISSION_CODES.NOTIFICATIONS_SEND,
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Push Notifications - publishers and admin
    pushNotifications: {
        permission: PERMISSION_CODES.NOTIFICATIONS_SEND,
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Post Data - requires specific permission
    postData: {
        anyPermissions: [PERMISSION_CODES.ARTICLES_VIEW_ALL, PERMISSION_CODES.ARTICLES_VIEW_OWN],
        anyRoles: ['ROLE_AUTHOR', 'ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Web Series parent — visible to publishers and admins
    webSeries: {
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // All Series list — publishers and admins with view permission
    allSeries: {
        anyPermissions: [PERMISSION_CODES.WEB_SERIES_VIEW],
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Create Series — publishers and admins with create permission
    createSeries: {
        anyPermissions: [PERMISSION_CODES.WEB_SERIES_CREATE],
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Web Series Home Sections — admin only (layout/configuration)
    webSeriesSections: {
        anyPermissions: [PERMISSION_CODES.WEB_SERIES_SECTIONS_MANAGE],
        anyRoles: ['ROLE_ADMIN'],
    },

    // Category Management - open to all authenticated users (role restrictions to be added later)
    categoryManagement: {},

    allCategories: {},

    // Home Section Management - admin only
    homeSectionManagement: {
        anyRoles: ['ROLE_ADMIN'],
    },

    // Workflow Management - parent menu, visible to all with workflow access
    workflowManagement: {
        permission: PERMISSION_CODES.WORKFLOW_VIEW,
        anyRoles: ['ROLE_AUTHOR', 'ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Author Dashboard - authors and above
    authorWorkflow: {
        anyRoles: ['ROLE_AUTHOR', 'ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Editor Review Queue - editors and above
    editorWorkflow: {
        permission: PERMISSION_CODES.ARTICLES_APPROVE,
        anyRoles: ['ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Publisher Queue - publishers and admin
    publisherWorkflow: {
        permission: PERMISSION_CODES.ARTICLES_PUBLISH,
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // Workflow Admin Dashboard - admin only
    adminWorkflowDashboard: {
        permission: PERMISSION_CODES.WORKFLOW_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Access Control Management - admin only
    accessControlManagement: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_VIEW,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Permission Management - admin only
    permissionManagement: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Role Permission Matrix - admin only
    rolePermissionMatrix: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // User Access Control - admin only
    userAccessControl: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Audit Logs - admin only
    auditLogs: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_VIEW,
        anyRoles: ['ROLE_ADMIN'],
    },
};

/**
 * Permission configuration for demo/auth pages
 * Note: These are intentionally open - login/logout should always be visible
 * We hide login/signup for authenticated users in the component
 */
export const demoMenuPermissions: Record<string, MenuPermissionConfig> = {
    // Auth section header - always visible
    auth: {},
    // Login - hide for authenticated users (handled in component logic)
    login: {
        // Mark as requiring NO role - we'll handle visibility differently
        hideWhenAuthenticated: true,
    } as MenuPermissionConfig,
    // Sign Up - hide for authenticated users
    signUp: {
        hideWhenAuthenticated: true,
    } as MenuPermissionConfig,
    // Logout - always visible for authenticated users
    logout: {},
    // 404 - hide from menu, it's an error page
    page404: {
        hideFromMenu: true,
    } as MenuPermissionConfig,
};

/**
 * Permission configuration for settings menu items (to be added)
 */
export const settingsMenuPermissions: Record<string, MenuPermissionConfig> = {
    // Settings - admin only
    settings: {
        permission: PERMISSION_CODES.SETTINGS_VIEW,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Access Control Management - admin only
    accessControl: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_VIEW,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Permission Management - admin only
    permissionManagement: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Role Management - admin only
    roleManagement: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // User Access Control - admin only
    userAccessControl: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },

    // Audit Logs - admin only
    auditLogs: {
        permission: PERMISSION_CODES.ACCESS_CONTROL_VIEW,
        anyRoles: ['ROLE_ADMIN'],
    },
};

/**
 * AI Features menu permissions
 */
export const aiMenuPermissions: Record<string, MenuPermissionConfig> = {
    // AI Content Generation - editors and above
    aiContentGeneration: {
        permission: PERMISSION_CODES.AI_FEATURES_USE,
        anyRoles: ['ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },

    // AI Settings - admin only
    aiSettings: {
        permission: PERMISSION_CODES.AI_FEATURES_MANAGE,
        anyRoles: ['ROLE_ADMIN'],
    },
};

/**
 * Moderation menu permissions
 *
 * Comments + Reports moderation are gated by role only because the
 * platform does not yet ship dedicated moderation permission codes
 * inside {@link PERMISSION_CODES}. Switch to permission-based gating
 * once those codes are added.
 */
export const moderationMenuPermissions: Record<string, MenuPermissionConfig> = {
    moderation: {
        anyRoles: ['ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },
    commentsModeration: {
        anyRoles: ['ROLE_EDITOR', 'ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },
    reportsModeration: {
        anyRoles: ['ROLE_PUBLISHER', 'ROLE_ADMIN'],
    },
};

/**
 * Get permission config for a menu item
 */
export const getMenuPermissionConfig = (menuId: string): MenuPermissionConfig | undefined => {
    return (
        dashboardMenuPermissions[menuId] ||
        demoMenuPermissions[menuId] ||
        settingsMenuPermissions[menuId] ||
        aiMenuPermissions[menuId] ||
        moderationMenuPermissions[menuId]
    );
};

/**
 * Check if menu item has permission requirements
 */
export const hasPermissionRequirements = (menuId: string): boolean => {
    const config = getMenuPermissionConfig(menuId);
    if (!config) return false;
    
    return !!(
        config.permission ||
        config.anyPermissions?.length ||
        config.allPermissions?.length ||
        config.role ||
        config.anyRoles?.length
    );
};

export default {
    dashboardMenuPermissions,
    demoMenuPermissions,
    settingsMenuPermissions,
    aiMenuPermissions,
    moderationMenuPermissions,
    getMenuPermissionConfig,
    hasPermissionRequirements,
};
